import React, { useRef, useState, useCallback } from "react";
import { Box, Button, Typography } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useImpersonation } from "./ImpersonationContext";

export function ImpersonationBanner() {
  const { isImpersonating, actorEmail, impersonatedEmail, exitImpersonation } =
    useImpersonation();

  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);

  // Center on first render once we know the card width
  const centerOnMount = useCallback((node: HTMLDivElement | null) => {
    if (node && !position) {
      const rect = node.getBoundingClientRect();
      setPosition({
        x: (window.innerWidth - rect.width) / 2,
        y: 8,
      });
    }
  }, [position]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!position) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        origX: position.x,
        origY: position.y,
      };
    },
    [position],
  );

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setPosition({
      x: dragRef.current.origX + dx,
      y: dragRef.current.origY + dy,
    });
  }, []);

  const onPointerUp = useCallback(() => {
    dragRef.current = null;
  }, []);

  if (!isImpersonating) return null;

  return (
    <>
    {/* Red border overlay around the entire viewport */}
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 9998,
        border: "8px solid #d32f2f",
        pointerEvents: "none",
        borderRadius: 0,
      }}
    />
    <Box
      ref={centerOnMount}
      sx={{
        position: "fixed",
        top: position?.y ?? 8,
        left: position?.x ?? "50%",
        transform: position ? "none" : "translateX(-50%)",
        visibility: position ? "visible" : "hidden",
        zIndex: 9999,
        backgroundColor: "#d32f2f",
        color: "white",
        padding: "6px 12px 6px 4px",
        display: "flex",
        alignItems: "center",
        gap: 1,
        borderRadius: 2,
        boxShadow: 3,
        userSelect: "none",
      }}
    >
      <Box
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        sx={{ cursor: "grab", display: "flex", alignItems: "center", "&:active": { cursor: "grabbing" } }}
      >
        <DragIndicatorIcon fontSize="small" />
      </Box>
      <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
        <strong>Impersonation</strong>: You ({actorEmail}) are impersonating{" "}
        {impersonatedEmail}
      </Typography>
      <Button
        variant="outlined"
        size="small"
        onClick={exitImpersonation}
        sx={{
          ml: 1,
          color: "white",
          borderColor: "white",
          whiteSpace: "nowrap",
          "&:hover": { borderColor: "white", backgroundColor: "rgba(255,255,255,0.1)" },
        }}
      >
        Exit
      </Button>
    </Box>
    </>
  );
}
